import React, { useEffect, useState } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import axios from "axios";
import { Spinner } from "../components/ui/shadcn-io/spinner";
import {
  Users,
  DollarSign,
  FileText,
  TrendingUp,
  AlertCircle,
} from "lucide-react";
import PendingApprovalsNotification from "../components/pendingApprovalsNotification";

// Types for our data
interface MonthlyExpense {
  month: string;
  totalExpense: number;
  employeeCount: number;
}

interface DashboardMetrics {
  totalEmployees: number;
  totalMonthlySalary: number;
  payslipsGenerated: number;
  pendingApprovals: number;
  monthlyExpenseData: MonthlyExpense[];
  currentMonthStats?: {
    totalPayslips: number;
    totalPaid: number;
    pendingPayslips: number;
    averagePayslip: number;
  };
  yearlyStats?: {
    totalPayslipsThisYear: number;
    totalPaidThisYear: number;
  };
  dataSource?: string;
}

export default function Dashboard() {
  const [metrics, setMetrics] = useState<DashboardMetrics | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string>("");

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setIsLoading(true);
      setError("");

      const token = localStorage.getItem("authToken");
      const headers = {
        Authorization: `Bearer ${token}`,
      };

      // Fetch all employees
      const employeesResponse = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}api/employees`,
        { headers }
      );
      const totalEmployees = employeesResponse.data.length;

      // Fetch comprehensive payslip statistics (no parameters needed)
      const payslipStatsResponse = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}api/payslips/stats/monthly`,
        { headers }
      );
      const payslipStats = payslipStatsResponse.data;

      // Prepare monthly expense data from actual payslips
      const monthlyExpenseData: MonthlyExpense[] = payslipStats.monthlyData.map(
        (data: any) => ({
          month: data.month,
          totalExpense: data.totalExpense,
          employeeCount: data.employeeCount,
        })
      );

      // Use real payslip data for metrics
      const payslipsGenerated =
        payslipStats.currentMonthStats?.totalPayslips || 0;
      const pendingApprovals =
        payslipStats.currentMonthStats?.pendingPayslips || 0;

      setMetrics({
        totalEmployees,
        totalMonthlySalary: payslipStats.currentMonthTotal || 0,
        payslipsGenerated,
        pendingApprovals,
        monthlyExpenseData,
        currentMonthStats: payslipStats.currentMonthStats,
        yearlyStats: payslipStats.yearlyStats,
        dataSource: payslipStats.dataSource,
      });
    } catch (error: any) {
      console.error("Error fetching dashboard data:", error);
      setError(
        error.response?.data?.message || "Failed to fetch dashboard data"
      );
    } finally {
      setIsLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(amount);
  };

  if (error) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <div className="rounded-lg border bg-destructive/10 p-6 text-center">
          <AlertCircle className="h-12 w-12 text-destructive mx-auto mb-4" />
          <h3 className="text-lg font-medium text-destructive">
            Error Loading Dashboard
          </h3>
          <p className="text-sm text-muted-foreground mt-2">{error}</p>
          <button
            onClick={fetchDashboardData}
            className="mt-4 px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (isLoading || !metrics) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Spinner variant="ellipsis" size={40} className="text-primary" />
          <div className="text-center">
            <h3 className="text-lg font-medium">Loading Dashboard</h3>
            <p className="text-sm text-muted-foreground">
              Fetching dashboard data...
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-1 flex-col gap-6 p-4 pt-0">
      {/* Header */}
      <div className="space-y-2">
        <h1 className="text-3xl font-bold tracking-tight">Dashboard</h1>
        <p className="text-muted-foreground">
          Real-time overview based on actual payslip data
          {metrics.dataSource && (
            <span className="ml-2 text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full">
              ✓ Data from {metrics.dataSource}
            </span>
          )}
        </p>
      </div>

      {/* Pending Approvals Notification */}
      <PendingApprovalsNotification variant="banner" autoRefresh={true} />

      {/* Key Metrics Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <div className="rounded-lg border bg-card text-card-foreground shadow-sm p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-muted-foreground">
                Total Employees
              </p>
              <p className="text-2xl font-bold">
                {metrics.totalEmployees || 0}
              </p>
            </div>
            <Users className="h-8 w-8 text-blue-600" />
          </div>
          <p className="text-xs text-muted-foreground mt-2">
            Active employees in system
          </p>
        </div>

        <div className="rounded-lg border bg-card text-card-foreground shadow-sm p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-muted-foreground">
                Current Monthly Paid
              </p>
              <p className="text-2xl font-bold">
                {formatCurrency(metrics.totalMonthlySalary || 0)}
              </p>
            </div>
            <DollarSign className="h-8 w-8 text-green-600" />
          </div>
          <p className="text-xs text-muted-foreground mt-2">
            Actual amount paid via approved payslips
          </p>
        </div>

        <div className="rounded-lg border bg-card text-card-foreground shadow-sm p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-muted-foreground">
                Payslips Generated
              </p>
              <p className="text-2xl font-bold">
                {metrics.payslipsGenerated || 0}
              </p>
            </div>
            <FileText className="h-8 w-8 text-purple-600" />
          </div>
          <p className="text-xs text-muted-foreground mt-2">
            This month's total payslips
          </p>
        </div>

        <div className="rounded-lg border bg-card text-card-foreground shadow-sm p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-muted-foreground">
                Pending Approvals
              </p>
              <p className="text-2xl font-bold text-orange-600">
                {metrics.pendingApprovals || 0}
              </p>
            </div>
            <AlertCircle className="h-8 w-8 text-orange-600" />
          </div>
          <p className="text-xs text-muted-foreground mt-2">
            Payslips awaiting approval
          </p>
        </div>
      </div>

      {/* Monthly Payslip Expense Chart */}
      <div className="rounded-lg border bg-card text-card-foreground shadow-sm p-6">
        <div className="flex items-center gap-2 mb-6">
          <TrendingUp className="h-6 w-6 text-primary" />
          <div>
            <h3 className="text-xl font-semibold">Monthly Payslip Expenses</h3>
            <p className="text-sm text-muted-foreground">
              Actual salary payments from approved payslips (Last 6 months)
            </p>
          </div>
        </div>

        <ResponsiveContainer width="100%" height={400}>
          <LineChart
            data={metrics.monthlyExpenseData}
            margin={{
              top: 20,
              right: 30,
              left: 20,
              bottom: 20,
            }}
          >
            <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
            <XAxis
              dataKey="month"
              tick={{ fontSize: 12 }}
              tickLine={{ strokeWidth: 1 }}
            />
            <YAxis
              tickFormatter={(value) => `₹${(value / 100000).toFixed(1)}L`}
              tick={{ fontSize: 12 }}
              tickLine={{ strokeWidth: 1 }}
            />
            <Tooltip
              formatter={(value: number) => [
                formatCurrency(value),
                "Total Paid",
              ]}
              labelFormatter={(label) => `Month: ${label}`}
              contentStyle={{
                backgroundColor: "hsl(var(--card))",
                border: "1px solid hsl(var(--border))",
                borderRadius: "8px",
                boxShadow: "0 4px 6px -1px rgb(0 0 0 / 0.1)",
              }}
            />
            <Line
              type="monotone"
              dataKey="totalExpense"
              stroke="hsl(var(--primary))"
              strokeWidth={3}
              dot={{
                fill: "hsl(var(--primary))",
                strokeWidth: 2,
                r: 5,
                stroke: "hsl(var(--background))",
              }}
              activeDot={{
                r: 7,
                fill: "hsl(var(--primary))",
                stroke: "hsl(var(--background))",
                strokeWidth: 2,
              }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
